import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Clock, MapPin, Building2, Users, FileText, Pencil, Copy, Hash, UserPlus, ExternalLink, Wrench } from 'lucide-react';
import FieldEventAttachments from './FieldEventAttachments';
import { equipmentSummary } from '../job/EquipmentFields';
import useEquipmentCatalog from '../../hooks/useEquipmentCatalog';
import { useAuth } from '../../contexts/AuthContext';
import { UnitAttachments } from '../customer/UnitAttachments';

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  const hh = h % 12 || 12;
  return m ? `${hh}:${String(m).padStart(2, '0')}${ampm}` : `${hh}${ampm}`;
};

export default function FieldEventDetails({ event, displayName, onClose, catById, userById, canEdit, canOpenJob, onEdit, onDuplicate, dark }) {
  const navigate = useNavigate();
  const { allTypes: equipmentTypes } = useEquipmentCatalog();
  const { user } = useAuth();
  if (!event) return null;
  const cat = catById[event.category_id];
  const canOpenFieldWork = event.attendees?.includes(user?.id) || ['management', 'field_management'].includes(user?.role);
  const end = event.end_date || event.date;
  const fmtDate = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <>
      <Dialog open={!!event} onOpenChange={(o) => !o && onClose()}>
        <DialogContent
          className={`grid-cols-[minmax(0,1fr)] max-h-[90vh] content-start overflow-y-auto overflow-x-hidden ${dark ? 'dark bg-slate-900 border-slate-700 text-slate-100' : ''}`}
          style={{ width: 'min(95vw, 42rem)', maxWidth: 'min(95vw, 42rem)' }}
          data-testid="field-event-details"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 pr-6">
              {cat && <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />}
              <span className="min-w-0 break-words" data-testid="field-event-display-name">{displayName || event.title}</span>
            </DialogTitle>
            <DialogDescription className="sr-only">Field event schedule, site, crew and customer assets.</DialogDescription>
          </DialogHeader>
          <div className="min-w-0 space-y-3 text-[15px]">
            <div className="flex items-start gap-2.5 text-slate-700 dark:text-slate-300">
              <Clock size={15} className="mt-0.5 text-slate-400 shrink-0" />
              <div>
                <p>{event.date === end ? fmtDate(event.date) : `${fmtDate(event.date)} → ${fmtDate(end)}`}</p>
                <p className="text-slate-500 dark:text-slate-400">{event.all_day ? 'All day' : `${fmtTime(event.start_time)} – ${fmtTime(event.end_time)}`}</p>
              </div>
            </div>
            {cat && (
              <div className="flex items-center gap-2.5 text-slate-700 dark:text-slate-300">
                <span className="w-[15px] flex justify-center"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.color }} /></span>
                {cat.label}
              </div>
            )}
            {event.job_number && (
              <div className="flex items-center gap-2.5 text-slate-700 dark:text-slate-300" data-testid="field-event-details-job">
                <Hash size={15} className="text-slate-400 shrink-0" />
                <span>Job #{event.job_number}</span>
                {event.job_id && (
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950 dark:text-emerald-400 dark:border-emerald-900 rounded">
                    Linked to shop job
                  </span>
                )}
              </div>
            )}
            {event.customer_name && (
              <div className="flex items-center gap-2.5 text-slate-700 dark:text-slate-300">
                <Building2 size={15} className="text-slate-400 shrink-0" />
                {event.customer_name}
              </div>
            )}
            {(event.location || event.lsd || event.site_details?.address) && (
              <div className="flex items-center gap-2.5 text-slate-700 dark:text-slate-300" data-testid="field-event-site-details">
                <MapPin size={15} className="text-slate-400 shrink-0" />
                <span className="min-w-0 break-words">{[event.location, event.lsd, event.site_details?.province, event.site_details?.address].filter(Boolean).join(' · ')}</span>
              </div>
            )}
            {(event.attendees || []).length > 0 && (
              <div className="flex items-start gap-2.5 text-slate-700 dark:text-slate-300">
                <Users size={15} className="mt-0.5 text-slate-400 shrink-0" />
                <div className="flex flex-wrap gap-1.5">
                  {event.attendees.map((a) => (
                    <span key={a} className="px-2 py-0.5 bg-slate-100 border border-slate-200 dark:bg-slate-800 dark:border-slate-700 rounded-full text-xs font-medium">
                      {userById[a]?.name || 'Unknown user'}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {(event.equipment || []).length > 0 && (
              <div className="flex items-start gap-2.5 min-w-0" data-testid="field-event-details-equipment">
                <Wrench size={15} className="mt-0.5 text-slate-400 shrink-0" />
                <ul className="min-w-0 flex-1 space-y-1.5">
                  {event.equipment.map((eq) => (
                    <li
                      key={eq.id}
                      className={`min-w-0 rounded-md border px-2.5 py-1.5 text-sm ${dark ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-slate-50'}`}
                    >
                      <p className="break-words font-medium">{equipmentSummary(eq, equipmentTypes)}</p>
                      {eq.notes && <p className="mt-0.5 whitespace-pre-wrap break-words text-[13px] text-slate-500 dark:text-slate-400">{eq.notes}</p>}
                      <UnitAttachments ids={eq.attachment_ids} prefix={`field-event-unit-files-${eq.id}`} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {event.notes && (
              <div className="flex items-start gap-2.5 text-slate-700 dark:text-slate-300 min-w-0">
                <FileText size={15} className="mt-0.5 text-slate-400 shrink-0" />
                <p className="whitespace-pre-wrap break-words min-w-0">{event.notes}</p>
              </div>
            )}
            {event.created_by && (
              <div className="flex items-center gap-2.5 text-xs text-slate-400 dark:text-slate-500 pt-1" data-testid="field-event-created-by">
                <UserPlus size={13} className="shrink-0" />
                <span>
                  Created by {userById[event.created_by]?.name || 'Unknown user'}
                  {event.created_at ? ` · ${new Date(event.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}
                </span>
              </div>
            )}
          </div>
          <div className="pt-2 border-t border-slate-100 dark:border-slate-800">
            <FieldEventAttachments eventId={event.id} canEdit={canEdit} dark={dark} equipment={event.equipment || []} />
          </div>
          {(canOpenFieldWork || canEdit || (event.job_id && canOpenJob)) && (
            <div
              className={`sticky -bottom-6 -mx-6 -mb-6 mt-1 flex flex-wrap justify-end gap-2 border-t px-6 py-3 ${
                dark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'}`}
              data-testid="field-event-details-actions"
            >
              {canOpenFieldWork && <Button size="sm" variant="outline" onClick={() => navigate(`/field/work/${event.id}`)} data-testid="field-event-open-work-btn"><FileText size={13} />Field Work</Button>}
              {event.job_id && canOpenJob && (
                <Button size="sm" variant="outline" onClick={() => navigate(`/jobs/${event.job_id}`)} data-testid="field-event-open-job-btn">
                  <ExternalLink size={13} className="mr-1.5" /> Open Shop Job
                </Button>
              )}
              {canEdit && (
                <>
                  <Button size="sm" variant="outline" onClick={() => onDuplicate(event)} data-testid="field-event-duplicate-btn">
                    <Copy size={13} className="mr-1.5" /> Duplicate
                  </Button>
                  <Button size="sm" className="bg-sky-600 hover:bg-sky-700 text-white" onClick={() => onEdit(event)} data-testid="field-event-edit-btn">
                    <Pencil size={13} className="mr-1.5" /> Edit
                  </Button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
